import { noop } from 'lodash';
import { createContext, type FormEvent, memo, type ReactNode, useCallback } from 'react';

import { useMemoizedBundle } from '@didaskill/ui/utils';

type RadioGroupContextValue = {
  value: string;
  onChange: (event: FormEvent<HTMLInputElement>) => void;
};

export const RadioGroupContext = createContext<RadioGroupContextValue>({
  value: null,
  onChange: noop,
});

export type RadioGroupContextProviderProps = {
  /** Selected value. */
  value?: string;
  /** On change function. */
  onChange?: (newValue: string) => void;
  /** Content. */
  children?: ReactNode;
};

export const RadioGroupContextProviderBase = ({
  value = null,
  onChange = noop,
  children = null,
}: RadioGroupContextProviderProps) => {
  const onChangeProxy = useCallback(
    (event: FormEvent<HTMLInputElement>) => onChange((event.target as HTMLInputElement).value),
    [onChange],
  );

  const contextValue = useMemoizedBundle({
    value,
    onChange: onChangeProxy,
  });

  return <RadioGroupContext.Provider value={contextValue}>{children}</RadioGroupContext.Provider>;
};

RadioGroupContextProviderBase.displayName = 'RadioGroupContextProvider';

export const RadioGroupContextProvider = memo(RadioGroupContextProviderBase);
